/**
 * NumberingSettings — Config numérotation.
 *
 * Approche IA-first (Sprint iter55) :
 *   1. Statut vierge → CTA proéminent « Configurer avec l'IA »
 *   2. Configuration IA validée → affichage lecture + badge 🔒 Verrouillé
 *   3. Mode expert (bouton discret) → conserve le formulaire manuel pour edge cases
 *
 * Verrou strict : après application via wizard IA, `billing.numbering_locked_at`
 * bloque toute modif via `POST /billing/numbering-config` (art. 242 nonies A CGI).
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Save, Sparkles, Info, AlertTriangle, RefreshCw, Hash, Lock,
  ChevronDown, ChevronUp, ArrowRight, Unlock,
} from "lucide-react";
import { toast } from "sonner";
import AIBillingSetupWizard from "@/components/sales/AIBillingSetupWizard";

const TOKENS = [
  { token: "{YYYY}", desc: "Année 4 chiffres — 2026" },
  { token: "{YY}", desc: "Année 2 chiffres — 26" },
  { token: "{MM}", desc: "Mois 2 chiffres — 09" },
  { token: "{DD}", desc: "Jour 2 chiffres — 03" },
  { token: "{SEQ}", desc: "Séquence (défaut 6 chiffres)" },
  { token: "{SEQ:5}", desc: "Séquence 5 chiffres — 11722" },
];

// Rendu d'un template Averdy Agency côté client (miroir minimal de numbering.py).
// Utilisé pour le preview live de la carte "Corriger le prochain numéro".
function renderTemplate(template, seq) {
  if (!template) return null;
  const now = new Date();
  const YYYY = String(now.getUTCFullYear());
  const YY = YYYY.slice(-2);
  const MM = String(now.getUTCMonth() + 1).padStart(2, "0");
  const DD = String(now.getUTCDate()).padStart(2, "0");
  return template.replace(/\{(YYYY|YY|MM|DD|SEQ)(?::(\d+))?\}/g, (_, tok, pad) => {
    if (tok === "YYYY") return YYYY;
    if (tok === "YY") return YY;
    if (tok === "MM") return MM;
    if (tok === "DD") return DD;
    if (tok === "SEQ") return String(seq).padStart(pad ? Number(pad) : 6, "0");
    return "";
  });
}

export default function NumberingSettings({ embedded = false } = {}) {
  const { hasPermission, tenant } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [addMode, setAddMode] = useState(null); // null | "add_quote" | "add_credit"
  const [expertMode, setExpertMode] = useState(false);
  const [form, setForm] = useState({
    invoice_template: "", quote_template: "", credit_note_template: "",
    reset_period: "yearly", starting_seq: 1,
    current_seq_override: "", override_reason: "",
  });
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, st] = await Promise.all([
        api.get("/billing/numbering-config"),
        api.get("/billing/setup-status"),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
      setForm((p) => ({
        ...p,
        invoice_template: cfg.data.kinds.invoice.template,
        quote_template: cfg.data.kinds.quote.template,
        credit_note_template: cfg.data.kinds.credit_note.template,
        reset_period: cfg.data.kinds.invoice.reset_period,
        starting_seq: cfg.data.kinds.invoice.starting_seq,
      }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!form.invoice_template || !expertMode) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const payload = {
          invoice_template: form.invoice_template,
          quote_template: form.quote_template,
          credit_note_template: form.credit_note_template,
          reset_period: form.reset_period,
          starting_seq: Number(form.starting_seq) || 1,
        };
        if (form.current_seq_override) payload.current_seq_override = Number(form.current_seq_override);
        const r = await api.post("/billing/numbering-config/preview", payload);
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form, expertMode]);

  const saveOverride = async () => {
    if (!form.override_reason || form.override_reason.trim().length < 5) {
      toast.error("Justification (min 5 caractères) obligatoire");
      return;
    }
    const target = Number(form.current_seq_override);
    if (!Number.isInteger(target) || target < 1) {
      toast.error("Numéro invalide");
      return;
    }
    setSaving(true);
    try {
      // L'utilisateur saisit le numéro qui apparaîtra sur la PROCHAINE facture
      // (mental model naturel). Le backend attend `current_seq_override` = valeur
      // du compteur AVANT bump → on retire 1.
      await api.post("/billing/numbering-config", {
        current_seq_override: target - 1,
        override_reason: form.override_reason.trim(),
      });
      toast.success(`Correction appliquée · Prochaine facture : ${overridePreview || "—"}`);
      setForm((p) => ({ ...p, current_seq_override: "", override_reason: "" }));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // Preview live du numéro rendu quand owner tape son nouveau numéro
  const overridePreview = useMemo(() => {
    const n = Number(form.current_seq_override);
    if (!Number.isInteger(n) || n < 1) return null;
    const tpl = config?.kinds?.invoice?.template;
    if (!tpl) return null;
    return renderTemplate(tpl, n);
  }, [form.current_seq_override, config]);

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-72 bg-slate-100 animate-pulse rounded" />
      <div className="h-64 bg-slate-100 animate-pulse rounded" />
    </div>
  );

  const locked = !!status?.numbering_locked_at;

  return (
    <div className="space-y-6" data-testid="numbering-settings">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Numérotation des documents
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Config par entité (<span className="font-mono">{tenant?.name || "—"}</span>).
            Respecte la loi française (numérotation continue par exercice).
          </p>
        </div>
      )}

      {/* CTA IA principal — visible tant que non verrouillé */}
      {!locked && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 via-white to-blue-50">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-slate-900 mb-1">
                  Configure ta numérotation en 30 secondes
                </h2>
                <p className="text-sm text-slate-600 mb-3">
                  Dépose ta dernière facture — notre IA détecte automatiquement
                  ton format et t'annonce ton prochain numéro. Fini les templates
                  à configurer à la main.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {canWrite && (
                    <Button
                      onClick={() => setWizardOpen(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="open-ai-wizard-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Configurer avec l'IA
                    </Button>
                  )}
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setExpertMode((v) => !v)}
                    className="text-xs text-slate-600"
                    data-testid="toggle-expert-mode">
                    {expertMode ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                    Mode expert (config manuelle)
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verrouillé — vue lecture */}
      {locked && (
        <>
          {/* Bloc principal : source → format → prochaine facture (toujours visible) */}
          <Card className="border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-blue-50 shadow-md">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-emerald-700" />
                <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
                  Numérotation active — Verrouillée
                </p>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] ml-auto">
                  Art. 242 nonies A CGI
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-4">
                <div className="text-center md:text-left">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                    {status?.numbering_source?.detected_number ? "Facture importée" : "Origine"}
                  </p>
                  <p className="font-mono text-xl font-bold text-slate-900 mt-1"
                      data-testid="lock-source-number">
                    {status?.numbering_source?.detected_number || "Configuration manuelle"}
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-emerald-500 mx-auto hidden md:block" />
                <div className="text-center md:text-right">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">
                    Prochaine facture Averdy Agency
                  </p>
                  <p className="font-mono text-xl font-bold text-emerald-700 mt-1"
                      data-testid="lock-next-number">
                    {config?.kinds?.invoice?.next_preview?.[0] || "—"}
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-emerald-200 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-600">
                <span>
                  Format :{" "}
                  <code className="font-mono bg-white/60 px-1.5 py-0.5 rounded text-slate-800">
                    {config?.kinds?.invoice?.template || "—"}
                  </code>
                </span>
                <span>
                  {status?.numbering_kinds_meta?.invoice?.added_at ? (
                    <>Ajouté le{" "}
                      <span className="font-medium">
                        {new Date(status.numbering_kinds_meta.invoice.added_at).toLocaleDateString("fr-FR")}
                      </span>
                    </>
                  ) : (
                    <>Verrouillé le{" "}
                      <span className="font-medium">
                        {new Date(status.numbering_locked_at).toLocaleString("fr-FR")}
                      </span>
                    </>
                  )}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Sous-bloc : devis / avoir */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { kind: "quote", label: "Devis", customFlag: "custom_quote_template", mode: "add_quote" },
              { kind: "credit_note", label: "Avoir", customFlag: "custom_credit_note_template", mode: "add_credit" },
            ].map(({ kind, label, customFlag, mode }) => {
              const isCustom = !!status?.[customFlag];
              const meta = status?.numbering_kinds_meta?.[kind];
              return (
                <Card key={kind} className={!isCustom ? "border-dashed border-purple-300 bg-purple-50/30" : ""}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                            {label}
                          </p>
                          {!isCustom && (
                            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[9px]">
                              Format par défaut
                            </Badge>
                          )}
                          {isCustom && meta?.added_at && (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]"
                                     data-testid={`kind-${kind}-added-at`}>
                              Ajouté le {new Date(meta.added_at).toLocaleDateString("fr-FR")}
                            </Badge>
                          )}
                        </div>
                        <p className="font-mono text-xs text-slate-800 mt-1">
                          {config?.kinds?.[kind]?.template || "—"}
                        </p>
                        {config?.kinds?.[kind]?.next_preview?.[0] && (
                          <p className="text-[11px] text-slate-500 mt-1">
                            Prochain : <span className="font-mono font-medium text-slate-700">
                              {config.kinds[kind].next_preview[0]}
                            </span>
                          </p>
                        )}
                        {isCustom && meta?.source_number && (
                          <p className="text-[10px] text-slate-400 mt-1">
                            Depuis : <span className="font-mono">{meta.source_number}</span>
                          </p>
                        )}
                      </div>
                      {canWrite && !isCustom && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => setAddMode(mode)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-100 text-xs"
                          data-testid={`add-${kind}-btn`}>
                          <Sparkles className="w-3 h-3 mr-1" /> Configurer avec l'IA
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bloc admin : réinitialiser (owner uniquement) */}
          {status?.is_owner && (
            <UnlockCard onDone={load} />
          )}
        </>
      )}

      {/* Mode expert — édition libre (uniquement si non verrouillé + toggle actif) */}
      {!locked && expertMode && (
        <>
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <AlertDescription className="text-xs text-amber-900">
              <strong>Mode expert</strong> — configuration manuelle. Recommandé
              uniquement si l'IA n'a pas détecté correctement ton format.
              Cette modif ne verrouille PAS la numérotation.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
                <Hash className="w-4 h-4 text-blue-600" /> Templates
              </CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: "invoice_template", label: "Facture" },
                  { key: "quote_template", label: "Devis" },
                  { key: "credit_note_template", label: "Avoir" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <Label htmlFor={`num-tpl-${key}`} className="text-sm">{label}</Label>
                    <Input id={`num-tpl-${key}`} name={`num-tpl-${key}`}
                            value={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                            className="font-mono text-sm" placeholder="FR-{YYYY}-{SEQ:6}"
                            disabled={!canWrite} data-testid={`input-${key}`} />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="reset_period" className="text-sm">Réinitialiser compteur</Label>
                    <Select value={form.reset_period} onValueChange={(v) => setForm((p) => ({ ...p, reset_period: v }))}
                             disabled={!canWrite}>
                      <SelectTrigger id="reset_period" name="reset_period" data-testid="reset-period"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yearly">Annuel (1er janvier)</SelectItem>
                        <SelectItem value="monthly">Mensuel</SelectItem>
                        <SelectItem value="never">Jamais (continu)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="starting_seq" className="text-sm">Numéro de départ</Label>
                    <Input id="starting_seq" name="starting_seq" type="number" value={form.starting_seq}
                            onChange={(e) => setForm((p) => ({ ...p, starting_seq: e.target.value }))}
                            disabled={!canWrite} data-testid="starting-seq" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Aperçu des 3 prochains numéros</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {["invoice", "quote", "credit_note"].map((kind) => (
                  <div key={kind}>
                    <p className="text-xs text-slate-500 uppercase mb-1">
                      {kind === "invoice" ? "Facture" : kind === "quote" ? "Devis" : "Avoir"}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(preview?.[kind]?.next || []).map((n) => (
                        <Badge key={n} className="font-mono text-xs bg-slate-100 text-slate-800 border-slate-200" variant="outline">
                          {n}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-600">Tokens disponibles</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {TOKENS.map(({ token, desc }) => (
                  <div key={token} className="text-xs">
                    <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-blue-700">{token}</code>
                    <span className="text-slate-500 ml-2">{desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Corriger le compteur — RÉSERVÉ AU PROPRIÉTAIRE (owner) */}
      {status?.is_owner && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <AlertTriangle className="w-4 h-4" /> Corriger le prochain numéro de facture
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-amber-800">
              Vous pouvez ajuster manuellement à partir de quel numéro
              repartira la prochaine facture — utile si vous avez émis une
              facture de test par erreur, ou pour corriger un décalage après
              un import. Chaque correction est enregistrée dans l'historique
              de votre compte (qui l'a fait, quand, pour quelle raison).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="current_seq_override" className="text-sm">Numéro qui apparaîtra sur la prochaine facture</Label>
                <Input id="current_seq_override" name="current_seq_override" type="number" value={form.current_seq_override}
                        onChange={(e) => setForm((p) => ({ ...p, current_seq_override: e.target.value }))}
                        placeholder="ex : 11726" data-testid="override-seq" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="override_reason" className="text-sm">Raison de la correction (obligatoire, min 5 caractères)</Label>
                <Input id="override_reason" name="override_reason" value={form.override_reason}
                        onChange={(e) => setForm((p) => ({ ...p, override_reason: e.target.value }))}
                        placeholder="ex : Facture test émise par erreur"
                        data-testid="override-reason" />
              </div>
            </div>
            {/* Preview live — ce que la prochaine facture affichera vraiment */}
            {overridePreview && (
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-300"
                   data-testid="override-preview">
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-slate-600">Après validation, ta prochaine facture sera :</span>
                </div>
                <span className="font-mono text-base font-bold text-emerald-700"
                      data-testid="override-preview-value">
                  {overridePreview}
                </span>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={saveOverride} disabled={saving || !form.current_seq_override}
                       size="sm" className="bg-amber-600 hover:bg-amber-700 text-white"
                       data-testid="apply-override-btn">
                <Save className="w-3 h-3 mr-1" />
                {saving ? "Application…" : "Appliquer la correction"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={load} disabled={saving}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Recharger
        </Button>
      </div>

      <AIBillingSetupWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onDone={() => { setWizardOpen(false); load(); }}
      />
      <AIBillingSetupWizard
        mode={addMode || "full"}
        open={!!addMode}
        onOpenChange={(v) => { if (!v) setAddMode(null); }}
        onDone={() => { setAddMode(null); load(); }}
      />
    </div>
  );
}


/* -------------------------------------------------------------------------
 * UnlockCard — carte propriétaire pour déverrouiller la numérotation.
 * Confirmation en 2 temps : ouvrir la zone + saisir raison ≥ 10 car.
 * L'action est intégralement auditée côté backend.
 * -----------------------------------------------------------------------*/
function UnlockCard({ onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const unlock = async () => {
    if (reason.trim().length < 10) {
      toast.error("Justification (min 10 caractères) obligatoire");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/billing/numbering-config/unlock", { reason: reason.trim() });
      toast.success("Numérotation déverrouillée — la modif est tracée dans l'audit");
      setReason(""); setOpen(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!open) {
    return (
      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Info className="w-3.5 h-3.5" />
          Zone administrateur — action réservée au propriétaire du compte.
        </div>
        <Button
          onClick={() => setOpen(true)}
          variant="ghost" size="sm"
          className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid="unlock-open-btn">
          <Unlock className="w-3.5 h-3.5 mr-1" />
          Réinitialiser la numérotation
        </Button>
      </div>
    );
  }

  return (
    <Card className="border-red-200 bg-red-50/40">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-red-900">
          <Unlock className="w-4 h-4" /> Réinitialiser la numérotation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="w-4 h-4 text-red-700" />
          <AlertDescription className="text-xs text-red-900">
            <strong>Action sensible</strong> — la loi FR (art. 242 nonies A CGI)
            impose une numérotation continue. Après déverrouillage, tu pourras
            re-configurer via le wizard IA. L'action laisse une trace <strong>permanente</strong>
            {" "}dans l'audit trail (raison, date, utilisateur).
          </AlertDescription>
        </Alert>
        <div>
          <Label htmlFor="unlock-reason" className="text-sm">Raison du déverrouillage (min 10 caractères) *</Label>
          <Input
            id="unlock-reason" name="unlock-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="ex: Erreur de configuration, ré-import après audit comptable, migration…"
            data-testid="unlock-reason-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            {reason.length}/10 caractères minimum
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setOpen(false); setReason(""); }}>
            Annuler
          </Button>
          <Button
            onClick={unlock}
            disabled={submitting || reason.trim().length < 10}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="unlock-confirm-btn">
            <Unlock className="w-4 h-4 mr-1.5" />
            {submitting ? "Déverrouillage…" : "Déverrouiller (audité)"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
